import { useMemo } from 'react';

import TasksHeader from './TasksHeader';
import NoPodBanner from './NoPodBanner';
import TasksTabs from './TasksTabs';
import TasksTable from './TasksTable';
import LoadingState from './states/LoadingState';
import EmptyState from './states/EmptyState';
import NoResultsState from './states/NoResultsState';
import ErrorState from './states/ErrorState';
import s from './TasksDashboard.module.scss';

import type { FC } from 'react';
import type {
  TabKey,
  SortField,
  SortOrder,
  IDashboardTasksResponse,
} from 'pages/TasksDashboard/types';

type TabCounts = Record<TabKey, number>;

interface ITasksDashboardOrganismProps {
  activeTab: TabKey;
  onTabChange: (tabKey: TabKey) => void;
  data?: IDashboardTasksResponse;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  podFilter?: string;
  onPodFilterChange: (value: string | undefined) => void;
  priorityFilter?: string;
  onPriorityFilterChange: (value: string | undefined) => void;
  reporterFilter?: string;
  onReporterFilterChange: (value: string | undefined) => void;
  offset: number;
  limit: number;
  onPageChange: (newOffset: number) => void;
  assigningGuids: Set<string>;
  onAssign: (taskGuid: string) => void;
  completingGuids: Set<string>;
  onComplete: (taskGuid: string) => void;
  onReturn: (taskGuid: string) => void;
  returningGuid: string | null;
  onEdit: (taskGuid: string) => void;
  sortBy?: SortField;
  sortOrder?: SortOrder;
  onSortChange: (
    field: SortField | undefined,
    order: SortOrder | undefined,
  ) => void;
  tabCounts?: TabCounts;
}

const TasksDashboardOrganism: FC<ITasksDashboardOrganismProps> = ({
  activeTab,
  onTabChange,
  data,
  isLoading,
  isError,
  onRetry,
  hasActiveFilters,
  onClearFilters,
  search,
  onSearchChange,
  podFilter,
  onPodFilterChange,
  priorityFilter,
  onPriorityFilterChange,
  reporterFilter,
  onReporterFilterChange,
  offset,
  limit,
  onPageChange,
  assigningGuids,
  onAssign,
  completingGuids,
  onComplete,
  onReturn,
  returningGuid,
  onEdit,
  sortBy,
  sortOrder,
  onSortChange,
  tabCounts,
}) => {
  const showNoPodBanner = useMemo(() => {
    if (!data || data.tasks.length === 0) return false;
    return (
      data.navBadgeCount === 0 &&
      data.tasks.every((task) => task.podContext === null)
    );
  }, [data]);

  const tasks = data?.tasks ?? [];

  const renderContent = () => {
    if (isLoading) return <LoadingState />;
    if (isError) return <ErrorState onRetry={onRetry} />;
    if (tasks.length === 0 && hasActiveFilters)
      return <NoResultsState onClearFilters={onClearFilters} />;
    if (tasks.length === 0)
      return (
        <EmptyState
          activeTab={activeTab}
          onGoToOpenPool={() => onTabChange('open_pool')}
        />
      );

    return (
      <TasksTable
        tasks={tasks}
        total={data?.total ?? 0}
        isLoading={false}
        activeTab={activeTab}
        offset={offset}
        limit={limit}
        onPageChange={onPageChange}
        assigningGuids={assigningGuids}
        onAssign={onAssign}
        completingGuids={completingGuids}
        onComplete={onComplete}
        onReturn={onReturn}
        returningGuid={returningGuid}
        onEdit={onEdit}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={onSortChange}
      />
    );
  };

  return (
    <div className={s.container}>
      <TasksHeader
        search={search}
        onSearchChange={onSearchChange}
        podFilter={podFilter}
        onPodFilterChange={onPodFilterChange}
        priorityFilter={priorityFilter}
        onPriorityFilterChange={onPriorityFilterChange}
        reporterFilter={reporterFilter}
        onReporterFilterChange={onReporterFilterChange}
        totalResults={data?.total}
      />
      {showNoPodBanner && <NoPodBanner />}
      <div className={s['content-wrapper']}>
        <TasksTabs
          activeTab={activeTab}
          onTabChange={onTabChange}
          data={data}
          tabCounts={tabCounts}
        />
        {renderContent()}
      </div>
    </div>
  );
};

export default TasksDashboardOrganism;
